import type { BrandStyle, MoodboardProfile } from '~/design-system/components/DeckGen/types/presentation'
import type { BrandStyleTokens } from '~/design-system/components/DeckGen/ai/deckFlowPrompt'
import { buildMoodboardPromptBlock, moodboardToBrandTokens, profileAsBoard } from '~/design-system/components/DeckGen/utils/moodboardStyle'
import { withBirtvyStyleGuard } from './birtvyStyleGuard'

/**
 * A BRAND KIT — the reusable style a user builds once and generates every deck from.
 *
 * It is deliberately its OWN type rather than an extension of {@link MoodboardProfile}. The reason is
 * mechanical, not aesthetic: generation consumes TWO things, a prompt block built from the profile and a
 * `BrandStyleTokens` object, and the logo only exists on the second. A profile cannot physically hold it.
 * So the kit owns the SOURCES of both and projects into both via {@link brandKitToGenerationInputs}.
 *
 * The profile is embedded UNFORKED — this is the same `MoodboardProfile` a moodboard or a single wired
 * image produces, so every existing synthesis path keeps working untouched.
 *
 * A saved `BirtvyDesignSystem` is already a valid `BrandKit`: the only addition is the optional
 * `brandStyle`, so promotion is a re-read, not a conversion, and generation output cannot move for a deck
 * that already exists (see `__tests__/brandKit.test.ts`).
 */
export interface BrandKit {
  id: string
  /** Editable display name; synthesis proposes one (e.g. "SPACE10 Editorial"). */
  name: string
  /** Which intake sources fed this kit — provenance for the guide. */
  sources?: { pdf?: string[]; images?: string[]; websites?: string[] }
  /**
   * The synthesized style spec: taste, palette, roles, fonts, tone, typography, spacing, shadows,
   * guideline, patterns. Read from pixels, so its values are ESTIMATES.
   *
   * Note `profile.typography` is a SENTENCE ("Display headlines 120px+ bold uppercase"). It is not the
   * same field as `brandStyle.typography`, which is a record of CSS facts. Same name, different things,
   * and they must never be merged.
   */
  profile: MoodboardProfile
  /**
   * The website's EXACT CSS-derived brand, when the kit was built from (or later pointed at) a URL.
   * Ground truth where it exists: pixels cannot name a font or read an exact hex, CSS can. It is also the
   * only carrier of `logoUrl` and of the `@font-face` files that tell us which heading weights actually
   * ship.
   *
   * Absent on every kit promoted from a deck-scoped design system, which is what keeps their generation
   * byte-identical: `moodboardToBrandTokens` ignores an undefined second argument entirely.
   */
  brandStyle?: BrandStyle
  /** Display-only example thumbnails pulled from a source PDF. Never reaches a model. */
  examples?: string[]
  /** Where the user dragged each widget in the kit's own canvas. Absent = the default arrangement. */
  layout?: BrandKitLayout
  createdAt: number
  updatedAt: number
}

/** Widget key → world position in the kit canvas. Sparse: only widgets the user moved are stored, and an
 *  unknown key is ignored, so the default arrangement can be re-laid-out later without breaking saved kits. */
export type BrandKitLayout = Record<string, { x: number; y: number }>

/** The screen rect an editing surface was opened from — the seam for an expand-from-card transition. */
export interface BrandKitOrigin {
  x: number
  y: number
  width: number
  height: number
}

/** What the widget hands back on save — the store mints id/timestamps. */
export interface BrandKitInput {
  name: string
  profile: MoodboardProfile
  brandStyle?: BrandStyle
  sources?: BrandKit['sources']
  examples?: string[]
  layout?: BrandKitLayout
}

/** Everything a kit contributes to one generation call. */
export interface BrandKitGenerationInputs {
  /**
   * The art-direction block to append to the prompt, already carrying Birtvy's literal-copy guard.
   * Empty string when the kit has nothing to say (or `strength` reads as off) — callers test truthiness,
   * exactly as they did against `withBirtvyStyleGuard` before.
   */
  promptBlock: string
  /** The `brandStyle` argument for the generation call. Undefined when the kit yields no tokens. */
  brandStyle: BrandStyleTokens | undefined
}

/**
 * Project a kit into the two things generation actually takes.
 *
 * This replaces the trio every call site used to spell out by hand
 * (`profileAsBoard` → `withBirtvyStyleGuard(buildMoodboardPromptBlock(...))` → `moodboardToBrandTokens`),
 * which is what let the exact CSS tokens get silently dropped: no design-system caller ever passed the
 * second argument, so `typography`, `headingFontWeights` and `logoUrl` were thrown away on every
 * generation from a saved style. Routing through one function means a kit that HAS them cannot lose them.
 *
 * `strength` matches what the call sites passed (100, i.e. strict). It stays a parameter because the
 * single-slide path honours a wired `styleStrength`, and a kit should be able to follow that later
 * without a second helper.
 */
export function brandKitToGenerationInputs(
  kit: Pick<BrandKit, 'profile' | 'brandStyle'>,
  strength: number | null = 100,
): BrandKitGenerationInputs {
  const board = profileAsBoard(kit.profile)
  return {
    promptBlock: withBirtvyStyleGuard(buildMoodboardPromptBlock(board, strength)),
    brandStyle: moodboardToBrandTokens(board, kit.brandStyle),
  }
}

/** The ACTIVE kit, or null. */
export function resolveActiveBrandKit(kits: BrandKit[], activeKitId: string | null): BrandKit | null {
  if (!activeKitId) return null
  return kits.find((k) => k.id === activeKitId) ?? null
}
